// FILE: components/layout/AppShell.tsx
"use client";

import { createPortal } from "react-dom";
import { useState, useCallback, useEffect, useRef, Suspense } from "react";
import { usePathname, useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import { AuthProvider } from "@/lib/AuthContext";
import { DiamondsProvider } from "@/lib/DiamondsContext";
import { cn } from "@/lib/utils";
import type { UserRole } from "@/types";

const Sidebar = dynamic(() => import("./Sidebar"));
const Topbar = dynamic(() => import("./Topbar"));
const QuestionBankFAB = dynamic(() => import("@/components/question-bank/QuestionBankFAB"), { ssr: false });
const InAppBrowserBanner = dynamic(() => import("@/components/shared/InAppBrowserBanner"), { ssr: false });
const GlobalQuestionBankDrawer = dynamic(() => import("@/components/question-bank/GlobalQuestionBankDrawer"), { ssr: false });
const InterviewVaultFAB = dynamic(() => import("@/components/interview/vault/InterviewVaultFAB"), { ssr: false });
const InterviewVaultDrawer = dynamic(() => import("@/components/interview/vault/InterviewVaultDrawer"), { ssr: false });
const GlobalSearchModal = dynamic(() => import("@/components/search/GlobalSearchModal"), { ssr: false });
const PathFullscreen = dynamic(() => import("@/components/study/PathFullscreen"), { ssr: false });
const ChatPanel = dynamic(() => import("@/components/chat/ChatPanel"), { ssr: false });
const ChatSidebar = dynamic(() => import("@/components/chat/ChatSidebar"), { ssr: false });
const AuthModal = dynamic(() => import("@/components/auth/AuthModal"), { ssr: false });
type AppMode = "chat" | "explore";

// User type nhận từ Server Component (layout.tsx)
export interface AppUser {
  id: string;
  email?: string;
  username?: string;
  full_name?: string;
  avatar_url?: string;
  role: UserRole;
  diamonds: number;
  pink_diamonds: number;
  referral_code?: string;
  current_streak?: number;
  // Profile fields (Settings + ProfileHeader)
  bio?: string | null;
  birthday?: string | null;
  gender?: string | null;
  phone?: string | null;
  domain_id?: string | null;
  verify_level?: number | null;
  created_at?: string | null;
}

// ═══ Map pathname → title cho Topbar ═══
const ROUTE_TITLES: Array<{ match: RegExp | string; title: string }> = [
  { match: "/", title: "Trợ lý Ôn thi AI" },
  { match: "/explore", title: "Khám phá" },
  { match: "/my-paths", title: "Lộ trình của tôi" },
  { match: "/studio", title: "Creator Studio" },
  { match: "/reviews", title: "Review" },
  { match: "/leaderboard", title: "Bảng xếp hạng" },
  { match: "/badges", title: "Huy hiệu của tôi" },
  { match: "/settings", title: "Cài đặt tài khoản" },
  { match: "/notifications", title: "Thông báo" },
  { match: "/wallet", title: "Ví kim cương" },
  { match: "/invite", title: "Giới thiệu bạn bè" },
  { match: "/help", title: "Trợ giúp" },
  { match: /^\/paths\/[a-f0-9-]{36}$/, title: "Không gian học tập" },
  { match: /^\/paths$/, title: "Lộ trình học" },
  { match: /^\/exams\/[a-f0-9-]{36}$/, title: "Chi tiết đề thi" },
  { match: /^\/exams$/, title: "Đề thi nổi bật" },
  { match: /^\/reviews\/.+$/, title: "Chi tiết đánh giá" },
  { match: /^\/orgs\/.+$/, title: "Chi tiết tổ chức" },
  { match: /^\/orgs$/, title: "Tổ chức nổi bật" },
];

function getTitleFromPath(pathname: string): string {
  for (const { match, title } of ROUTE_TITLES) {
    if (typeof match === "string") {
      if (pathname === match) return title;
    } else if (match.test(pathname)) {
      return title;
    }
  }
  // /{username} → profile
  const segs = pathname.split("/").filter(Boolean);
  if (segs.length === 1 && !["admin", "api", "auth"].includes(segs[0])) {
    return "Trang cá nhân";
  }
  return "JB AI";
}

function isImmersivePath(pathname: string): boolean {
  return /^\/paths\/[a-f0-9-]{36}$/.test(pathname) || /^\/exams\/[a-f0-9-]{36}$/.test(pathname);
}

// ─── user prop nhận từ layout.tsx (Server Component) ───
export default function AppShell({ children, user }: { children: React.ReactNode; user: AppUser | null }) {
  const pathname = usePathname();
  const router = useRouter();

  const [mode, setMode] = useState<AppMode>("explore");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [studyOpen, setStudyOpen] = useState(false);
  const [qBankOpen, setQBankOpen] = useState(false);
  const [vaultOpen, setVaultOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [chatSidebarOpen, setChatSidebarOpen] = useState(false);

  const title = getTitleFromPath(pathname);
  const isImmersiveMode = isImmersivePath(pathname);
  const isPathDetail = /^\/paths\/[a-f0-9-]{36}$/.test(pathname);
  const pathDetailId = isPathDetail ? pathname.split("/").pop() ?? null : null;
  const isInterviewPath = pathname.startsWith("/interview"); 

// Keyboard shortcut Q → toggle Question Bank
  useEffect(() => {
  const handler = (e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return;
    if (e.key === "q" || e.key === "Q") {
      if (studyOpen || isImmersiveMode || mode === "chat" || isInterviewPath) return; // ← THÊM isInterviewPath
      e.preventDefault();
      setQBankOpen(p => !p);
    }
  };
  window.addEventListener("keydown", handler);
  return () => window.removeEventListener("keydown", handler);
}, [studyOpen, isImmersiveMode, mode, isInterviewPath]); // ← THÊM dependency

  // ═══ Navigation handler — chỉ còn router.push thuần ═══
  const handleNavChange = useCallback((id: string) => {
    // Special action: study fullscreen overlay
    if (id === "study") {
      setStudyOpen(true);
      return;
    }

    // Pattern "entity-detail:uuid" từ GlobalSearchModal hoặc các chỗ khác
    if (id.startsWith("path-detail:")) {
      router.push("/paths/" + id.split(":")[1]);
      setMobileOpen(false);
      return;
    }
    if (id.startsWith("exam-detail:")) {
      router.push("/exams/" + id.split(":")[1]);
      setMobileOpen(false);
      return;
    }
    if (id.startsWith("review-detail:")) {
      router.push("/reviews/" + id.split(":")[1]);
      setMobileOpen(false);
      return;
    }
    if (id.startsWith("quick-exam:")) {
      router.push("/studio/quick-exam/" + id.split(":")[1]);
      setMobileOpen(false);
      return;
    }
    if (id.startsWith("org-detail:")) {
      router.push("/orgs/" + id.split(":")[1]);
      setMobileOpen(false);
      return;
    }

    // Profile → redirect to /[username]
    if (id === "profile") {
      if (user?.username) router.push(`/${user.username}`);
      else if (user?.id) router.push(`/${user.id}`);
      setMobileOpen(false);
      return;
    }

    // Map legacy nav id → real route
    const NAV_TO_ROUTE: Record<string, string> = {
      home: "/",
      explore: "/explore",
      reviews: "/reviews",
      leaderboard: "/leaderboard",
      "list-paths": "/paths",
      "list-exams": "/exams",
      "list-orgs": "/orgs",
      "my-learning": "/my-paths",
      creator: "/studio",
      "creator-exams": "/studio?tab=exam",
      "creator-reviews": "/studio?tab=review",
      "create-path": "/studio/paths/new",
      "quick-exam": "/studio/exams/new",
      badges: "/badges",
      settings: "/settings",
      help: "/help",
      wallet: "/wallet",
      referral: "/invite",
      notifications: "/notifications",
    };

    const route = NAV_TO_ROUTE[id];
    if (route) {
      router.push(route);
      setMobileOpen(false);
      return;
    }

    // Fallback: không nhận dạng được → về home
    router.push("/");
    setMobileOpen(false);
  }, [mode, router, user]);

  return (
    <AuthProvider user={user}>
      <DiamondsProvider 
  initialDiamonds={user?.diamonds ?? 0}
  initialPinkDiamonds={user?.pink_diamonds ?? 0}
>
      <div className="flex h-screen overflow-hidden">
        {!studyOpen && (
          <>
            {mode === "chat" && user ? (
              <>
                {/* Mobile overlay */}
                <div
                  className={cn(
                    "fixed inset-0 z-40 bg-teal-900/55 backdrop-blur-[2px] transition-opacity md:hidden",
                    chatSidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                  )}
                  onClick={() => setChatSidebarOpen(false)}
                />
                {/* Chat Sidebar */}
                <div className={cn(
                  "flex-shrink-0 h-full transition-all duration-[280ms]",
                  "max-md:fixed max-md:top-0 max-md:bottom-0 max-md:left-0 max-md:z-50",
                  "max-md:w-[75vw] max-md:max-w-[17rem]",
                  chatSidebarOpen
                    ? "max-md:translate-x-0 max-md:shadow-[4px_0_24px_rgba(0,0,0,.2)]"
                    : "max-md:-translate-x-full",
                  sidebarCollapsed ? "md:w-0 md:min-w-0 md:overflow-hidden" : "md:w-60 md:min-w-[15rem]"
                )}>
                  <ChatSidebar
                    userId={user.id}
                    activeChatId={activeChatId}
                    onSelectChat={(id) => { setActiveChatId(id); setChatSidebarOpen(false); }}
                    onBack={() => setMode("explore")}
                  />
                </div>
              </>
            ) : (
              <Sidebar
                collapsed={sidebarCollapsed}
                mobileOpen={mobileOpen}
                onMobileClose={() => setMobileOpen(false)}
                isAdmin={user?.role === "ADMIN"}
                user={user}
                onOpenAuth={() => setAuthOpen(true)}
              />
            )}
          </>
        )}

        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {!studyOpen && (
            <div className={cn(isImmersiveMode && "hidden md:block")}>
              <Topbar
                title={title}
                mode={mode}
                onModeChange={setMode}
                sidebarCollapsed={sidebarCollapsed}
                onToggleSidebar={() => setSidebarCollapsed(p => !p)}
                chatSidebarOpen={chatSidebarOpen}
                onToggleChatSidebar={() => setChatSidebarOpen(p => !p)}
                onMobileMenuOpen={() => setMobileOpen(true)}
                onNavigate={handleNavChange}
                onOpenSearch={() => setSearchOpen(true)}
                onOpenAuth={() => setAuthOpen(true)}
                user={user}
              />
            </div>
          )}

          <div className="flex-1 overflow-hidden relative">
            <div className="absolute inset-0 overflow-y-auto overflow-x-hidden scrollbar-thin">
              {mode === "chat" && user ? (
                <ChatPanel
                  userId={user.id}
                  chatId={activeChatId}
                  onChatCreated={(id) => setActiveChatId(id)}
                />
              ) : (
                children
              )}
            </div>
          </div>
        </div>

        {studyOpen && typeof document !== "undefined" && createPortal(
          <PathFullscreen onExit={() => setStudyOpen(false)} pathId={pathDetailId} />,
          document.body
        )}
        {!studyOpen && !isImmersiveMode && mode !== "chat" && user && !isInterviewPath && (
  <QuestionBankFAB onClick={() => setQBankOpen(true)} />
)}
        <GlobalQuestionBankDrawer isOpen={qBankOpen} context="default" onClose={() => setQBankOpen(false)} />
        {!studyOpen && !isImmersiveMode && mode !== "chat" && user && (
          <InterviewVaultFAB onClick={() => setVaultOpen(true)} />
        )}
        <InterviewVaultDrawer isOpen={vaultOpen} onClose={() => setVaultOpen(false)} />
        <GlobalSearchModal
          isOpen={searchOpen}
          onClose={() => setSearchOpen(false)}
          onNavigate={(page) => { setSearchOpen(false); handleNavChange(page); }}
        />
        <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
        <InAppBrowserBanner />
      </div>
    </DiamondsProvider>
    </AuthProvider>
  );
}